import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SkuParseDto } from 'marketplace/shared/graphCms/skuParse.dto';
import { getProductDetailsHOF } from '../../../store/selectors/product.selectors';
import StyleSelectorDropdown from './StyleSelectorDropdown';
import StyleSelectorConfigurations from './StyleSelectorConfigurations';

import { ProductStyleCMS } from './types';
import {
  initialStyleAndConfig,
  onSelectConfigHandler,
  onSelectStyleHandler,
} from './StyleSelector.helpers';

export const optionsMapper = ({ id, label, icon }: ProductStyleCMS) => ({
  value: id,
  label,
  iconUrl: icon.url,
});

export interface StyleSelectorContainerProps {
  onSelectCb?: ({
    sku,
    fallbackSku,
  }: Partial<{
    sku: string;
    fallbackSku: string;
  }>) => Promise<void> | void;
  skuParse?: SkuParseDto;
}

const StyleSelectorContainer = ({
  onSelectCb,
  skuParse,
}: StyleSelectorContainerProps) => {
  const productStyles = useSelector(getProductDetailsHOF('productStyles')) || [];
  const skuSplit = useSelector(getProductDetailsHOF('skuSplit'));
  const { style, config } = initialStyleAndConfig(skuSplit, productStyles);
  const [activeStyle, setActiveStyle] = useState(style);
  const [activeConfiguration, setActiveConfiguration] = useState(config);
  const [hasInitialized, setHasInitialized] = useState(false);

  const onSelectStyle = onSelectStyleHandler(
    productStyles,
    setActiveStyle,
    onSelectCb,
  );

  const onSelectConfig = onSelectConfigHandler(
    activeStyle,
    setActiveConfiguration,
    skuSplit,
    onSelectCb,
    skuParse,
  );

  useEffect(() => {
    setActiveStyle(style);
    setActiveConfiguration(config);
  }, [skuSplit, productStyles]);

  useEffect(() => {
    if (productStyles.length === 1 && !hasInitialized) {
      const onlyStyle = productStyles[0];
      setActiveStyle(onlyStyle);
      setHasInitialized(true);
    }
  }, [productStyles, setActiveStyle, onSelectCb, hasInitialized]);

  return (
    <div data-testid={'style-selector-container'}>
      {productStyles.length > 1 && (
        <StyleSelectorDropdown
          options={productStyles.map(optionsMapper)}
          activeStyle={activeStyle}
          onSelect={onSelectStyle}
        />
      )}
      <StyleSelectorConfigurations
        configurations={activeStyle?.configurations || []}
        activeConfiguration={activeConfiguration}
        onSelect={onSelectConfig}
      />
    </div>
  );
};

export default StyleSelectorContainer;
